import { toast } from "react-toastify";
import { SET_LOA_DATA, LOA_LIST_FETCHING_INIT } from "./types";
/**
 * Parses API response data (handles different response structures)
 */
function parseApiResponse(response) {
    return response?.data || response;
}
/**
 * Creates LOA action creators that use the provided apiHandler function.
 * This allows the actions to work with different API implementations
 * (e.g., admin-portal vs dashboard).
 *
 * @param apiHandler - The API handler function with signature (method, url, data)
 * @returns Object containing all LOA action creators
 */
export function createLOAActions(apiHandler) {
    const fetchLOAList = (params) => async (dispatch) => {
        try {
            dispatch({ type: LOA_LIST_FETCHING_INIT });
            const queryParams = new URLSearchParams();
            if (params.page)
                queryParams.append("page", params.page.toString());
            if (params.perPage)
                queryParams.append("limit", params.perPage.toString());
            if (params.orgId)
                queryParams.append("org_id", params.orgId);
            if (params.region)
                queryParams.append("region", params.region);
            if (params.status)
                queryParams.append("status", params.status);
            if (params.createdBy)
                queryParams.append("created_by", params.createdBy);
            if (params.search)
                queryParams.append("customer_name", params.search);
            const url = `/loa${queryParams.toString() ? `?${queryParams.toString()}` : ""}`;
            const response = await apiHandler("get", url);
            // Response structure: { code: 200, message: "...", data: { loa_records: [...], total, page, limit } }
            const responseData = parseApiResponse(response);
            const loaRecords = responseData?.loa_records || responseData?.data?.loa_records || [];
            const totalRecords = responseData?.total || responseData?.data?.total || 0;
            const currentPage = responseData?.page || responseData?.data?.page || params.page || 1;
            const limit = responseData?.limit ||
                responseData?.data?.limit ||
                params.perPage ||
                10;
            dispatch({
                type: SET_LOA_DATA,
                payload: {
                    loaList: loaRecords,
                    totalRecords,
                    page: currentPage,
                    perPage: limit,
                    isListLoading: false,
                    initialListLoading: false,
                },
            });
        }
        catch (err) {
            console.error("Error fetching LOA list:", err);
            dispatch({
                type: SET_LOA_DATA,
                payload: {
                    isListLoading: false,
                    initialListLoading: false,
                    loaList: [],
                },
            });
            const errMessage = err?.message ||
                "Failed to fetch LOA records. Please try again.";
            toast.error(errMessage);
        }
    };
    const fetchLOAById = (id) => async (dispatch) => {
        try {
            dispatch({
                type: SET_LOA_DATA,
                payload: { isDetailLoading: true },
            });
            const response = await apiHandler("get", `/loa/${id}`);
            // Response structure: { code: 200, message: "...", data: { loa_record: {...} } }
            const responseData = parseApiResponse(response);
            const loaRecord = responseData?.loa_record || responseData?.data?.loa_record;
            dispatch({
                type: SET_LOA_DATA,
                payload: {
                    selectedLOA: loaRecord || null,
                    isDetailLoading: false,
                },
            });
            return loaRecord;
        }
        catch (err) {
            console.error("Error fetching LOA:", err);
            dispatch({
                type: SET_LOA_DATA,
                payload: { isDetailLoading: false },
            });
            const errMessage = err?.message ||
                "Failed to fetch LOA record. Please try again.";
            toast.error(errMessage);
            throw err;
        }
    };
    const createLOA = (loaData) => async (dispatch) => {
        try {
            dispatch({
                type: SET_LOA_DATA,
                payload: { isSubmitting: true },
            });
            const response = await apiHandler("post", "/loa", loaData);
            const responseData = parseApiResponse(response);
            const newLOA = responseData?.loa_record || responseData?.data?.loa_record;
            dispatch({
                type: SET_LOA_DATA,
                payload: {
                    isSubmitting: false,
                    listRefetch: true,
                },
            });
            toast.success("LOA record created successfully");
            return newLOA;
        }
        catch (err) {
            console.error("Error creating LOA:", err);
            dispatch({
                type: SET_LOA_DATA,
                payload: { isSubmitting: false },
            });
            const errMessage = err?.message ||
                "Failed to create LOA record. Please try again.";
            toast.error(errMessage);
            throw err;
        }
    };
    const updateLOA = (id, loaData) => async (dispatch) => {
        try {
            dispatch({
                type: SET_LOA_DATA,
                payload: { isSubmitting: true },
            });
            const response = await apiHandler("put", `/loa/${id}`, loaData);
            // Response structure: { code: 200, message: "...", data: { loa_record: {...} } }
            const responseData = parseApiResponse(response);
            const updatedLOA = responseData?.loa_record || responseData?.data?.loa_record;
            dispatch({
                type: SET_LOA_DATA,
                payload: {
                    isSubmitting: false,
                    listRefetch: true,
                },
            });
            toast.success("LOA record updated successfully");
            return updatedLOA;
        }
        catch (err) {
            console.error("Error updating LOA:", err);
            dispatch({
                type: SET_LOA_DATA,
                payload: { isSubmitting: false },
            });
            const errMessage = err?.message ||
                "Failed to update LOA record. Please try again.";
            toast.error(errMessage);
            throw err;
        }
    };
    const deleteLOA = (id) => async (dispatch) => {
        try {
            dispatch({
                type: SET_LOA_DATA,
                payload: { isDeleting: true },
            });
            await apiHandler("del", `/loa/${id}`, {});
            dispatch({
                type: SET_LOA_DATA,
                payload: {
                    isDeleting: false,
                    listRefetch: true,
                },
            });
            toast.success("LOA record deleted successfully");
        }
        catch (err) {
            console.error("Error deleting LOA:", err);
            dispatch({
                type: SET_LOA_DATA,
                payload: { isDeleting: false },
            });
            const errMessage = err?.message ||
                "Failed to delete LOA record. Please try again.";
            toast.error(errMessage);
            throw err;
        }
    };
    /**
     * Set current page for pagination
     */
    const setLOAPage = (page) => (dispatch) => {
        dispatch({
            type: SET_LOA_DATA,
            payload: { page, listRefetch: true },
        });
    };
    return {
        fetchLOAList,
        fetchLOAById,
        createLOA,
        updateLOA,
        deleteLOA,
        setLOAPage,
    };
}
